import type {
  CpscRecallRecord,
  FdaEnforcementRecord,
  FdaEnforcementResponse,
  RecallItem,
  UsdaRecallRecord,
  VehicleRecallRecord,
} from '../interfaces';

export function decodeHtmlEntities(text: string): string {
  if (!text.includes('&')) {
    return text;
  }
  let decoded = text;

  decoded = decoded
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');

  decoded = decoded
    .replace(/&#(\d+);/g, (_, dec) =>
      String.fromCodePoint(Number.parseInt(dec, 10))
    )
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) =>
      String.fromCodePoint(Number.parseInt(hex, 16))
    );

  return decoded;
}

export function normalizeUsdaRecallRecord(
  record: UsdaRecallRecord
): UsdaRecallRecord {
  const normalized: UsdaRecallRecord = { ...record };
  for (const key of Object.keys(normalized)) {
    const val = normalized[key];
    if (typeof val === 'string') {
      normalized[key] = decodeHtmlEntities(val);
    }
  }
  return normalized;
}

export function normalizeUsdaRecallRecords(
  records: UsdaRecallRecord[]
): UsdaRecallRecord[] {
  return records.map(normalizeUsdaRecallRecord);
}

/** Sort FDA results by recall_initiation_date descending (newest first). */
export function sortFdaByInitiationDate(
  results: FdaEnforcementRecord[]
): FdaEnforcementRecord[] {
  return [...results].sort((a, b) => {
    const dateA = (a.recall_initiation_date as string) ?? '';
    const dateB = (b.recall_initiation_date as string) ?? '';
    return dateB.localeCompare(dateA);
  });
}

export function mergeUsdaRecallsByRecallNumber(
  ...arrays: UsdaRecallRecord[][]
): UsdaRecallRecord[] {
  const byNumber = new Map<string, UsdaRecallRecord>();
  for (const arr of arrays) {
    for (const record of arr) {
      const key = record.field_recall_number ?? '';
      if (key && !byNumber.has(key)) {
        byNumber.set(key, record);
      }
    }
  }
  return Array.from(byNumber.values());
}

/** Normalize date string to YYYY-MM-DD for comparable sort (FDA uses YYYYMMDD, CPSC uses ISO). */
export function normalizeRecallDate(dateStr: string): string {
  if (!dateStr) {
    return '';
  }
  const trimmed = dateStr.trim();
  if (/^\d{8}$/.test(trimmed)) {
    return `${trimmed.slice(0, 4)}-${trimmed.slice(4, 6)}-${trimmed.slice(
      6,
      8
    )}`;
  }
  return trimmed.slice(0, 10);
}

export function fdaRecordsToRecallItems(
  data: FdaEnforcementResponse | undefined,
  source: 'fdaDrug' | 'fdaDevice' | 'fdaFood'
): RecallItem[] {
  return (data?.results ?? []).map((record) => ({
    source,
    date:
      (record.recall_initiation_date as string) ??
      (record.report_date as string) ??
      '',
    record,
  }));
}

export function usdaRecordsToRecallItems(
  records: UsdaRecallRecord[] | undefined
): RecallItem[] {
  return (records ?? []).map((record) => ({
    source: 'usda' as const,
    date: (record.field_recall_date as string) ?? '',
    record,
  }));
}

export function cpscRecordsToRecallItems(
  records: CpscRecallRecord[] | undefined
): RecallItem[] {
  return (records ?? []).map((record) => ({
    source: 'cpsc' as const,
    date: (record.RecallDate as string) ?? '',
    record,
  }));
}

export function vehicleRecallsToRecallItems(
  records: VehicleRecallRecord[]
): RecallItem[] {
  return (records ?? []).map((record) => ({
    source: 'vehicle' as const,
    date: record.date ?? '',
    record,
  }));
}

/** Build combined list of all featured recalls with date, sorted by date descending (newest first). */
export function buildAllSortedByDate(
  fdaDrug: FdaEnforcementRecord[],
  fdaDevice: FdaEnforcementRecord[],
  fdaFood: FdaEnforcementRecord[],
  usda: UsdaRecallRecord[],
  cpsc: CpscRecallRecord[],
  vehicle: VehicleRecallRecord[]
): RecallItem[] {
  const withDate: RecallItem[] = [
    ...fdaRecordsToRecallItems({ results: fdaDrug }, 'fdaDrug'),
    ...fdaRecordsToRecallItems({ results: fdaDevice }, 'fdaDevice'),
    ...fdaRecordsToRecallItems({ results: fdaFood }, 'fdaFood'),
    ...usdaRecordsToRecallItems(usda),
    ...cpscRecordsToRecallItems(cpsc),
    ...vehicleRecallsToRecallItems(vehicle),
  ];
  return withDate.sort((a, b) => {
    const normA = normalizeRecallDate(a.date);
    const normB = normalizeRecallDate(b.date);
    return normB.localeCompare(normA);
  });
}

/** Sort recall items by date descending (newest first). */
export function sortRecallItemsByDate(items: RecallItem[]): RecallItem[] {
  return [...items].sort((a, b) => {
    const normA = normalizeRecallDate(a.date);
    const normB = normalizeRecallDate(b.date);
    return normB.localeCompare(normA);
  });
}
